// Sentry helpers — prod-ready config, PII scrub, noise filter, tag push.
// 3 panel (AdminPanel, Besyildiz, Template/demo) bu helper'ı kullanır; farklılık
// sadece deployable tag'i + DSN env var'ı.
//
// Plan "Sentry Prod-Ready Aktivasyonu → Ortak Sözleşmeler" ile birebir. Router
// faz'ında webhook tag'lere bakacak — naming geriye dönük değiştirilmemeli.

export interface SentryConfig {
  dsn: string;
  deployable: string;            // 'adminpanel' | 'besyildiz' | 'demo'
  environment: string;
  release?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  enabled?: boolean;
  debug?: boolean;
}

export interface SentryUserContext {
  id?: string | number;
  firmId?: number;
  role?: string;
}

const PROJECT_TAG = 'raporgor';

const IGNORE_ERRORS: (string | RegExp)[] = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'Non-Error exception captured',
  'NetworkError when attempting to fetch resource',
  'Failed to fetch',
  'Load failed',
  'ChunkLoadError',
  /Loading chunk \d+ failed/,
];

const DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
];

const SENSITIVE_KEY_PATTERN =
  /(password|passwordhash|token|refreshtoken|secret|apikey|api[_-]?key|cardnumber|card[_-]?no|cvv|cvc|iban|licensekey|license[_-]?key|tckno|tc[_-]?kimlik[_-]?no)/i;

const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
]);

function maskSensitiveRecord(record: Record<string, unknown> | undefined): void {
  if (!record) return;
  for (const key of Object.keys(record)) {
    if (SENSITIVE_HEADERS.has(key.toLowerCase()) || SENSITIVE_KEY_PATTERN.test(key)) {
      record[key] = '[Filtered]';
    }
  }
}

function maskSensitiveString(value: string): string {
  return value.replace(
    /([?&](?:token|key|password|secret|apikey|api_key|licensekey|license_key)=)[^&]+/gi,
    '$1[Filtered]',
  );
}

// Single-arg signature — Sentry beforeSend hook'u EventHint'i opsiyonel tutar ama
// bizim işimizi yalnız event'in kendisi yapıyor (plan).
function scrubPii(event: Record<string, unknown>): Record<string, unknown> | null {
  try {
    const req = event.request as Record<string, unknown> | undefined;
    if (req) {
      maskSensitiveRecord(req.headers as Record<string, unknown> | undefined);
      if (typeof req.query_string === 'string') {
        req.query_string = maskSensitiveString(req.query_string);
      }
      if (typeof req.url === 'string') {
        req.url = maskSensitiveString(req.url);
      }
      if (req.data && typeof req.data === 'object') {
        maskSensitiveRecord(req.data as Record<string, unknown>);
      }
    }

    // User context — email/name asla gönderilmemeli (setSentryUser garantili
    // ama defansif olarak burada da temizle).
    const user = event.user as Record<string, unknown> | undefined;
    if (user) {
      delete user.email;
      delete user.username;
      delete user.name;
    }

    const breadcrumbs = event.breadcrumbs as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(breadcrumbs)) {
      for (const crumb of breadcrumbs) {
        if (typeof crumb.message === 'string') {
          crumb.message = maskSensitiveString(crumb.message);
        }
        if (crumb.data && typeof crumb.data === 'object') {
          maskSensitiveRecord(crumb.data as Record<string, unknown>);
        }
      }
    }
  } catch {
    // scrub hatası event'i düşürmesin
  }
  return event;
}

export function getDefaultSentryConfig(
  dsn: string,
  options: {
    deployable: string;
    environment?: string;
    release?: string;
    isDevelopment?: boolean;
  },
): SentryConfig {
  const isDevelopment = options.isDevelopment ?? false;

  return {
    dsn,
    deployable: options.deployable,
    environment: options.environment ?? (isDevelopment ? 'development' : 'production'),
    release: options.release,
    tracesSampleRate: isDevelopment ? 1.0 : 0.1,
    // replaysSessionSampleRate 0 — maliyet kontrolü (plan).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    enabled: Boolean(dsn),
    debug: isDevelopment,
  };
}

export function initSentry(
  Sentry: {
    init: (config: Record<string, unknown>) => void;
    setTags: (tags: Record<string, string>) => void;
    browserTracingIntegration?: () => unknown;
    replayIntegration?: (options?: Record<string, unknown>) => unknown;
  },
  config: SentryConfig,
): void {
  if (!config.dsn || !config.enabled) {
    return;
  }

  const integrations: unknown[] = [];
  if (Sentry.browserTracingIntegration) {
    integrations.push(Sentry.browserTracingIntegration());
  }
  if (Sentry.replayIntegration) {
    integrations.push(Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }));
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    integrations,
    tracesSampleRate: config.tracesSampleRate,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    sendDefaultPii: false,
    attachStacktrace: true,
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    beforeSend: scrubPii,
    debug: config.debug,
  });

  // Router faz'ında webhook bu tag'lere bakacak — naming geriye dönük değiştirilmemeli.
  Sentry.setTags({
    project: PROJECT_TAG,
    deployable: config.deployable,
    'service.name': `${PROJECT_TAG}-${config.deployable}`,
  });
}

export function setSentryUser(
  Sentry: {
    setUser: (user: Record<string, unknown> | null) => void;
    setTag: (key: string, value: string) => void;
  },
  user: SentryUserContext | null,
): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  // Plan PII kuralı: email/name asla set edilmez, sadece stable id + tag'ler.
  const id = user.id?.toString();
  if (id) {
    Sentry.setUser({ id });
    Sentry.setTag('user.id', id);
  }
  if (user.firmId !== undefined) {
    Sentry.setTag('tenant', user.firmId.toString());
  }
  if (user.role) {
    Sentry.setTag('role', user.role);
  }
}

export function clearSentryUser(
  Sentry: { setUser: (user: null) => void },
): void {
  Sentry.setUser(null);
}

export function captureException(
  Sentry: {
    captureException: (
      error: unknown,
      context?: { extra?: Record<string, unknown>; tags?: Record<string, string> },
    ) => void;
  },
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  Sentry.captureException(error, { extra });
}

export function addBreadcrumb(
  Sentry: {
    addBreadcrumb: (breadcrumb: {
      category?: string;
      message: string;
      level?: 'debug' | 'info' | 'warning' | 'error';
      data?: Record<string, unknown>;
    }) => void;
  },
  breadcrumb: {
    category?: string;
    message: string;
    level?: 'debug' | 'info' | 'warning' | 'error';
    data?: Record<string, unknown>;
  },
): void {
  Sentry.addBreadcrumb(breadcrumb);
}
